/** Server-backed cart models — mirror GET/POST/PATCH /api/v1/cart(/items). */

export type CartOptionKind = 'addon' | 'modifier';

/** A selection on a cart line (priced addon or free modifier). */
export interface CartOption {
  kind: CartOptionKind;
  id: string;
  groupId: string;
  name: string;
  quantity: number;
  unitPrice: number;
}

/** One resolved + priced cart line. */
export interface CartItem {
  id: string;
  menuItemId: string;
  variantId: string;
  name: string;
  variantName: string | null;
  imageUrl: string | null;
  quantity: number;
  note: string | null;
  unitPrice: number;
  options: CartOption[];
  lineTotal: number;
  /** false → the branch no longer sells this line; excluded from every total. */
  available: boolean;
  unavailableReason: string | null;
}

/** Server-computed bill (POS parity). vatAmount is null on inclusive-pricing brands. */
export interface CartBill {
  currencyCode: string;
  vatInclusive: boolean;
  subtotal: number;
  serviceCharge: number;
  vatAmount: number | null;
  deliveryFee: number | null;
  deliveryFeeOriginal: number | null;
  freeDeliveryApplied: boolean;
  /** Promo/offer discount applied to the bill (null when none). */
  discount: number | null;
  total: number;
}

export interface FreeDelivery { enabled: boolean; threshold: number | null; remaining: number | null; }
export interface MinOrder { amount: number | null; met: boolean; }

/** The full cart payload the cart screen renders verbatim. */
export interface Cart {
  id: string | null;
  branchId: string | null;
  orderType: string | null;
  addressId: string | null;
  kitchenNote: string | null;
  deliveryNote: string | null;
  items: CartItem[];
  hasUnavailableItems: boolean;
  bill: CartBill;
  freeDelivery: FreeDelivery;
  minOrder: MinOrder;
  /** Applied promo/offer code (null when none). */
  promoCode: string | null;
}

/** Body for POST /cart/items. */
export interface AddCartItem {
  branchId?: string | null;
  /** The order type the customer selected (delivery | pickup | dine_in). */
  orderType?: string | null;
  /** Selected delivery address (delivery orders only). */
  addressId?: string | null;
  menuItemId: string;
  variantId: string;
  quantity: number;
  note?: string | null;
  options: { kind: CartOptionKind; id: string; groupId: string; quantity?: number }[];
}

/** Body for PUT /cart (order-type / branch / address / notes / dine-in table). */
export interface CartContext {
  orderType?: string | null;
  branchId?: string | null;
  addressId?: string | null;
  kitchenNote?: string | null;
  deliveryNote?: string | null;
  /** Dine-in: the verified table token + device coords bind the cart to the table. */
  dineInTableToken?: string | null;
  latitude?: number;
  longitude?: number;
}

/** Empty-state cart used before the first load. */
export const EMPTY_CART: Cart = {
  id: null, branchId: null, orderType: null, addressId: null, kitchenNote: null, deliveryNote: null,
  items: [], hasUnavailableItems: false,
  bill: {
    currencyCode: 'EGP', vatInclusive: false, subtotal: 0, serviceCharge: 0, vatAmount: 0,
    deliveryFee: null, deliveryFeeOriginal: null, freeDeliveryApplied: false, discount: null, total: 0,
  },
  freeDelivery: { enabled: false, threshold: null, remaining: null },
  minOrder: { amount: null, met: true },
  promoCode: null,
};
